import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Modal,
  Select,
  Segmented,
  DatePicker,
  Button,
  Space,
  Typography
} from "antd";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { TrainingAPI } from "../../api/TrainingAPI";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export const ActiveRosterShortcutModal = ({ visible, onClose, players, onSuccess }) => {
  const { allPlayers, positions, playerGroups } = useSelector(({ team }) => team);
  const date_format = "YYYY-MM-DD";
  
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);

  const trainingAPI = new TrainingAPI();

  // Parse player selections to get actual player IDs
  const parsePlayerSelections = (selections, allPlayers) => {
    const playerIds = [];
    
    selections.forEach(selection => {
      const value = selection.value || selection;
      
      if (value === 'full-team') {
        // Add all player IDs
        playerIds.push(...allPlayers.map(p => p.player_id));
      } else if (value.startsWith('player-')) {
        // Extract player ID
        const playerId = parseInt(value.replace('player-', ''));
        playerIds.push(playerId);
      } else if (value.startsWith('pos-')) {
        // Extract position
        const position = value.replace('pos-', '');
        const positionPlayers = allPlayers
          .filter(p => p.position === position)
          .map(p => p.player_id);
        playerIds.push(...positionPlayers);
      } else if (value.startsWith('group-')) {
        // Extract group ID
        const groupId = parseInt(value.replace('group-', ''));
        const groupPlayers = allPlayers
          .filter(p => p.player_groups?.includes(groupId))
          .map(p => p.player_id);
        playerIds.push(...groupPlayers);
      }
    });
    
    // Remove duplicates
    return [...new Set(playerIds)];
  };

  const handleSave = async () => {
    if (!selectedPlayers.length) {
      message.warning("Please select at least one player");
      return;
    }

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning("Please select a date range");
      return;
    }

    setLoading(true);
    try {
      // Parse player IDs from selections
      const playerIds = parsePlayerSelections(selectedPlayers, allPlayers);
      
      if (playerIds.length === 0) {
        message.warning("No valid players selected");
        setLoading(false);
        return;
      }

      const startDate = dateRange[0].format(date_format);
      const endDate = dateRange[1].format(date_format);
      
      await trainingAPI.updateActiveDatapoints(
        playerIds,
        startDate,
        endDate,
        isActive
      );
      
      message.success(`Successfully updated ${playerIds.length} player(s) for date range ${startDate} to ${endDate}`);
      
      // Reset form
      setSelectedPlayers([]);
      setIsActive(true);
      setDateRange(null);
      
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 400) {
        message.error(err.response.data?.error || "Invalid request");
      } else {
        message.error("Error updating player statuses");
      }
    }
    setLoading(false);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleCancel = () => {
    // Reset form on cancel
    setSelectedPlayers([]);
    setIsActive(true);
    setDateRange(null);
    onClose();
  };

  // Build player options for the select
  const playerOptions = [
    {
      label: 'Team',
      options: [
        { label: 'Full Team', value: 'full-team' }
      ]
    },
    {
      label: 'Players',
      options: (allPlayers || []).map(player => ({
        label: `${player.first_name} ${player.last_name}`,
        value: `player-${player.player_id}`
      }))
    },
    {
      label: 'Positions',
      options: (positions || []).map(pos => ({
        label: pos,
        value: `pos-${pos}`
      }))
    },
    {
      label: 'Groups',
      options: (playerGroups || []).map(group => ({
        label: group.name,
        value: `group-${group.player_group_id}`
      }))
    }
  ];

  const isSaveDisabled = selectedPlayers.length === 0 || !dateRange;

  return (
    <Modal
      title="Quick update your active roster for multiple days"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Form layout matching the design */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Text>Set</Text>
            
            <Select
              mode="multiple"
              labelInValue
              showSearch
              filterOption={(input, option) => {
                if (option?.label) {
                  return option.label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              onChange={(data) => setSelectedPlayers(data)}
              value={selectedPlayers}
              placeholder="Select Player(s)"
              style={{ minWidth: 200, flex: 1, borderRadius: '6px' }}
              options={playerOptions}
            />
            
            <Text>to</Text>
            
            <Segmented
              options={[
                { label: 'Active', value: true },
                { label: 'Inactive', value: false }
              ]}
              value={isActive}
              onChange={(value) => setIsActive(value)}
              style={{ borderRadius: '6px' }}
            />
            
            <Text>for</Text>
            
            <RangePicker
              onChange={handleDateRangeChange}
              value={dateRange}
              format={date_format}
              style={{ width: 250, borderRadius: '6px' }}
              placeholder={['Start Date', 'End Date']}
            />
          </div>

          {/* Save Changes Button */}
          <div style={{ marginTop: '20px' }}>
            <Button 
              type="primary"
              onClick={handleSave}
              loading={loading}
              disabled={isSaveDisabled}
              style={{ borderRadius: '6px' }}
            >
              Save Changes
            </Button>
          </div>
        </Space>
      </div>
    </Modal>
  );
};
